import React, { useState } from 'react';
import { PROTOCOLS, ROUTINES } from '../data/protocols';
import { Segment, protocolSegments, FULL_PROTOCOL } from '../data/runs';
import { GUIDED_SESSIONS } from '../data/guidedSessions';
import { useLanguage } from '../i18n/LanguageContext';
import ProtocolPlayer from '../components/ProtocolPlayer';
import SafetyNotice from '../components/SafetyNotice';

const ProtocolsPage: React.FC = () => {
  const { language, t } = useLanguage();
  const [activeSegments, setActiveSegments] = useState<Segment[] | null>(null);
  const [safetyAck, setSafetyAck] = useState<boolean>(() => {
    try { return localStorage.getItem('protocols_safetyAck') === '1'; } catch { return true; }
  });
  const acknowledgeSafety = () => {
    try { localStorage.setItem('protocols_safetyAck', '1'); } catch { /* ignore */ }
    setSafetyAck(true);
  };
  const pick = (b: { no: string; en: string }) => (language === 'en' ? b.en : b.no);

  if (activeSegments) {
    return (
      <>
        {!safetyAck && <SafetyNotice onAcknowledge={acknowledgeSafety} />}
        <div className="container mx-auto max-w-3xl">
          <ProtocolPlayer segments={activeSegments} onExit={() => setActiveSegments(null)} />
        </div>
      </>
    );
  }

  const sequenceIds = ROUTINES.morning.protocolIds;

  return (
    <>
      {!safetyAck && <SafetyNotice onAcknowledge={acknowledgeSafety} />}
      <div className="container mx-auto max-w-3xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-3">{t('protocols.pageTitle')}</h1>
          <p className="text-lg text-stone-600">{t('protocols.pageSubtitle')}</p>
        </div>

        <button
          onClick={() => setActiveSegments(FULL_PROTOCOL)}
          className="w-full text-left bg-gradient-to-br from-accent/15 to-teal-500/15 p-6 rounded-4xl border border-accent/20 shadow-lg hover:shadow-xl transition-shadow mb-6"
        >
          <h2 className="text-2xl font-bold mb-2">{t('protocols.fullTitle')}</h2>
          <p className="text-stone-600 text-sm">{t('protocols.fullDesc')}</p>
          <span className="inline-block mt-4 text-accent font-semibold">{t('protocols.startFull')} →</span>
        </button>

        {/* Routine cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          {Object.values(ROUTINES).map((routine) => (
            <button
              key={routine.id}
              onClick={() => setActiveSegments(protocolSegments(routine.protocolIds))}
              className="text-left bg-white/60 p-6 rounded-4xl shadow-lg border border-white/50 hover:shadow-xl transition-shadow"
            >
              <h2 className="text-2xl font-bold mb-2">{pick(routine.name)}</h2>
              <p className="text-stone-600 text-sm">{pick(routine.description)}</p>
              <span className="inline-block mt-4 text-accent font-semibold">
                {t('protocols.startRoutine')} →
              </span>
            </button>
          ))}
        </div>

        {/* Individual protocols */}
        <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-3">
          {t('protocols.individualTitle')}
        </h3>
        <div className="space-y-3 mb-10">
          {sequenceIds.map((id) => {
            const p = PROTOCOLS[id];
            return (
              <button
                key={id}
                onClick={() => setActiveSegments([{ kind: 'protocol', id }])}
                className="w-full text-left bg-white/50 p-4 rounded-2xl hover:bg-white/70 transition-colors flex items-center justify-between"
              >
                <div>
                  <span className="font-semibold text-stone-700 block">{pick(p.name)}</span>
                  <span className="text-sm text-stone-500">{pick(p.subtitle)}</span>
                </div>
                <span className="text-accent text-sm whitespace-nowrap ml-3">{pick(p.durationLabel)}</span>
              </button>
            );
          })}
        </div>

        {/* The Return */}
        <div className="bg-gradient-to-br from-accent/10 to-teal-500/10 p-6 rounded-4xl border border-accent/20">
          <h3 className="text-2xl font-bold mb-2">{pick(PROTOCOLS.return.name)}</h3>
          <p className="text-stone-600 text-sm mb-4">{pick(PROTOCOLS.return.subtitle)}</p>
          <button
            onClick={() => setActiveSegments([{ kind: 'protocol', id: 'return' }])}
            className="px-6 py-3 rounded-full text-base font-semibold text-white bg-accent hover:bg-accent-dark"
          >
            {t('protocols.startReturn')}
          </button>
        </div>

        <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-3">
          {t('protocols.sessionsTitle')}
        </h3>
        <div className="space-y-3 mb-10">
          {['heartBrain', 'fieldExpansion'].map((id) => {
            const s = GUIDED_SESSIONS[id];
            return (
              <button
                key={id}
                onClick={() => setActiveSegments([{ kind: 'session', id }])}
                className="w-full text-left bg-white/50 p-4 rounded-2xl hover:bg-white/70 transition-colors flex items-center justify-between"
              >
                <div>
                  <span className="font-semibold text-stone-700 block">{pick(s.name)}</span>
                  <span className="text-sm text-stone-500">{pick(s.subtitle)}</span>
                </div>
                <span className="text-accent text-sm whitespace-nowrap ml-3">{pick(s.durationLabel)}</span>
              </button>
            );
          })}
        </div>

        <div className="bg-gradient-to-br from-stone-200/60 to-stone-100/60 p-6 rounded-4xl border border-stone-200">
          <h3 className="text-2xl font-bold mb-2">{pick(GUIDED_SESSIONS.stillness.name)}</h3>
          <p className="text-stone-600 text-sm mb-4">{pick(GUIDED_SESSIONS.stillness.subtitle)}</p>
          <button
            onClick={() => setActiveSegments([{ kind: 'session', id: 'stillness' }])}
            className="px-6 py-3 rounded-full text-base font-semibold text-white bg-stone-600 hover:bg-stone-700"
          >
            {t('protocols.startStillness')}
          </button>
        </div>
      </div>
    </>
  );
};

export default ProtocolsPage;
